import { useAuth } from '@/features/auth/hooks/use-auth';
import { useNotificationStore } from '@/features/notifications/store/notification-store';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Bell, CheckCheck, Trash2, Sun, Moon, Monitor, Menu } from 'lucide-react';
import { IconoDeAviso } from '@/features/notifications/components/icono-de-aviso';
import { OrgSwitcher } from '@/components/layout/org-switcher';
import { pedirSalida } from '@/lib/salida-con-cambios';
import { useThemeStore } from '@/stores/theme-store';
import { useSidebarStore } from '@/stores/sidebar-store';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { destinoDeLaNotificacion } from '@/features/notifications/utils/destino-de-la-notificacion';
import { motion, AnimatePresence } from 'motion/react';
import { formatRelativeTime } from '@/lib/utils/format';
import { GlobalSearch } from './global-search';
import { RanuraDeAyuda } from './ranura-de-la-barra';

// ── Breadcrumb map ────────────────────────────────────────────
/**
 * La fuente de verdad de las migas de pan. Un guardián
 * (`route-labels.guard.test.ts`) la compara con el router: toda ruta protegida
 * estática tiene que tener entrada acá, o PENDIENTES con motivo.
 */
export const ROUTE_LABELS: Record<string, { label: string; parent?: string }> = {
  '/dashboard': { label: 'Inicio' },
  '/cotiza': { label: 'Cotiza', parent: '/dashboard' },
  '/cotiza/vision-general': { label: 'Visión general', parent: '/cotiza' },
  '/cotiza/gestion': { label: 'Gestión', parent: '/cotiza' },
  // Datos
  '/claves-api': { label: 'Claves API', parent: '/integrations' },
  '/integracion-api': { label: 'Integración API', parent: '/integrations' },
  // Datos Maestros
  '/master-data': { label: 'Datos Maestros', parent: '/dashboard' },
  '/materials': { label: 'Materiales', parent: '/master-data' },
  // Las pestañas de Datos Maestros tienen su dirección: sin esto la miga decía «Detalle».
  '/master-data/currencies': { label: 'Divisas', parent: '/master-data' },
  '/master-data/exchange-rates': { label: 'Tipos de cambio', parent: '/master-data' },
  '/master-data/lobs': { label: 'Líneas de negocio', parent: '/master-data' },
  '/master-data/countries': { label: 'Países', parent: '/master-data' },
  '/master-data/materials': { label: 'Materiales', parent: '/master-data' },
  '/master-data/quote-types': { label: 'Tipos de cotización', parent: '/master-data' },
  '/master-data/profit-centers': { label: 'Centros de beneficio', parent: '/master-data' },
  '/master-data/incoterms': { label: 'Incoterms', parent: '/master-data' },
  '/master-data/freight-leg-catalog': { label: 'Tramos y tarifas', parent: '/master-data' },
  '/master-data/freight-providers': { label: 'Proveedores de flete', parent: '/master-data' },
  '/master-data/freight-services': { label: 'Servicios de flete', parent: '/master-data' },
  '/master-data/freight-locations': { label: 'Ubicaciones', parent: '/master-data' },
  '/tariff-assistant': { label: 'Asistente arancelario', parent: '/comercia' },
  // Pricing
  '/pricing-models': { label: 'Modelos', parent: '/cotiza' },
  '/dynamic-pricing': { label: 'Precios Dinámicos', parent: '/dashboard' },
  // Sin entrada la miga caía al «Prixia Platform» de respaldo, como /permisos.
  '/revisar-vinculos': { label: 'Revisar vínculos', parent: '/compite/productos' },
  // Compite (rediseño de Precios Dinámicos): /dynamic-pricing redirige a estas vistas.
  // Miga «Dashboard › Compite › Monitoreo»: /compite lleva a Monitoreo.
  '/compite': { label: 'Compite', parent: '/dashboard' },
  '/compite/monitoreo': { label: 'Monitoreo', parent: '/compite' },
  '/compite/productos': { label: 'Productos', parent: '/compite' },
  '/compite/competencia': { label: 'Competencia', parent: '/compite' },
  '/compite/analitica': { label: 'Analítica', parent: '/compite' },
  // Viven en Integraciones (el menú): «Precios Dinámicos» ya no es una pantalla.
  '/tiendas-conectadas': { label: 'Tiendas conectadas', parent: '/integrations' },
  '/historial-push': { label: 'Historial de envíos', parent: '/tiendas-conectadas' },
  '/precio-inteligente': { label: 'Estrategias de Mercado', parent: '/dashboard' },
  // Compite (rediseño del 14-oct): «Inicio / Compite / Decisiones», como la maqueta.
  // `/compite` redirige a Decisiones (la primera vista del módulo), así la miga
  // del medio lleva a algún lado.
  '/compite/decisiones': { label: 'Decisiones', parent: '/compite' },
  '/compite/decisiones/revisar': { label: 'Revisar precios', parent: '/compite/decisiones' },
  '/compite/estrategia': { label: 'Estrategia', parent: '/compite' },
  // Analítica & IA
  '/analytics': { label: 'Analítica', parent: '/cotiza' },
  '/ai-intelligence': { label: 'Inteligencia de Pricing', parent: '/cotiza' },
  '/ai-productivity': { label: 'Productividad IA', parent: '/dashboard' },
  // Comercial
  '/quotes': { label: 'Cotizaciones', parent: '/cotiza/gestion' },
  '/quotes/new': { label: 'Nueva Cotización', parent: '/quotes' },
  // Pricer Datec
  // La lista del Pricer vive en Cotiza › Gestión: el detalle (/pricer-datec/:id) cuelga de acá.
  '/pricer-datec': { label: 'Cotizaciones Datec', parent: '/cotiza/gestion' },
  '/pricer-datec/quotes': { label: 'Cotizaciones Datec', parent: '/cotiza/gestion' },
  '/pricer-datec/new': { label: 'Nueva Cotización Datec', parent: '/pricer-datec/quotes' },
  // Es «Documentos» de Mi empresa (artefacto): cómo se ve lo que se envía al cliente.
  '/pricer-datec/configuracion': { label: 'Documentos', parent: '/mi-empresa' },
  '/pricer-solar': { label: 'Cotizador Solar', parent: '/cotiza' },
  '/pricer-solar/new': { label: 'Nueva Cotización Solar', parent: '/pricer-solar' },
  '/pricer-solar/config': { label: 'Parámetros del Cotizador Solar', parent: '/pricer-solar' },
  '/opportunities': { label: 'Oportunidades', parent: '/cotiza/gestion' },
  '/customers': { label: 'Clientes', parent: '/cotiza/gestion' },
  '/approvals': { label: 'Aprobaciones', parent: '/cotiza' },
  '/contracts': { label: 'Contratos', parent: '/cotiza/gestion' },
  '/contracts/new': { label: 'Nuevo Contrato', parent: '/contracts' },
  // Comercia (artefacto, 19-oct): «Inicio / Comercia / Cotizador».
  '/comercia': { label: 'Comercia', parent: '/dashboard' },
  // Comercia en tres pasos (maqueta de Melanie): «Inicio / Comercia / Logística / Origen y destino».
  '/comercia/productos': { label: 'Productos', parent: '/comercia' },
  '/comercia/productos/proforma': { label: 'Productos', parent: '/comercia' },
  '/comercia/productos/asistente': { label: 'Productos', parent: '/comercia' },
  '/comercia/logistica': { label: 'Logística', parent: '/comercia' },
  '/comercia/logistica/origen-destino': {
    label: 'Origen y destino',
    parent: '/comercia/logistica',
  },
  '/comercia/logistica/carga': { label: 'Carga', parent: '/comercia/logistica' },
  '/comercia/logistica/rutas': { label: 'Rutas', parent: '/comercia/logistica' },
  '/comercia/costo-nacionalizado': { label: 'Costo nacionalizado', parent: '/comercia' },
  '/trade/embarques': { label: 'Import / Export', parent: '/comercia' },
  '/trade/cotizaciones': { label: 'Cotizaciones', parent: '/comercia' },
  '/trade/cotizador': { label: 'Cotizador', parent: '/comercia' },
  // 28-oct: el Tarifario vive en Datos Maestros (Comercia lo usa en Rutas).
  '/trade/tarifario': { label: 'Tarifario', parent: '/master-data' },
  '/trade/aduana': { label: 'Aduana', parent: '/comercia' },
  '/trade/contenedores': { label: 'Contenedores', parent: '/comercia' },
  '/trade/indicadores': { label: 'Indicadores', parent: '/comercia' },
  // Admin
  // Mi empresa (artefacto): «Inicio / Mi empresa / Usuarios».
  '/mi-empresa': { label: 'Mi empresa', parent: '/dashboard' },
  '/users': { label: 'Usuarios', parent: '/mi-empresa' },
  // Sin esta línea la miga de pan mostraba solo "Prixia Platform", el texto de
  // respaldo, y la pantalla quedaba sin nombre arriba.
  '/permisos': { label: 'Roles', parent: '/mi-empresa' },
  '/configuracion/equipos': { label: 'Equipos', parent: '/mi-empresa' },
  '/configuracion/aprobaciones': { label: 'Aprobaciones', parent: '/mi-empresa' },
  // Las pestañas de Mi empresa (maqueta de Melanie, 25-oct).
  '/mi-empresa/resumen': { label: 'Resumen', parent: '/mi-empresa' },
  '/mi-empresa/usuarios': { label: 'Usuarios', parent: '/mi-empresa' },
  '/mi-empresa/roles': { label: 'Roles', parent: '/mi-empresa' },
  '/mi-empresa/equipos': { label: 'Equipos', parent: '/mi-empresa' },
  '/mi-empresa/aprobaciones': { label: 'Aprobaciones', parent: '/mi-empresa' },
  '/mi-empresa/documentos': { label: 'Documentos', parent: '/mi-empresa' },
  '/mi-empresa/pagos': { label: 'Pagos y facturación', parent: '/mi-empresa' },
  '/settings': { label: 'Configuración', parent: '/mi-empresa' },
  '/developer': { label: 'API Developers', parent: '/dashboard' },
  '/project-status': { label: 'Estado Proyecto', parent: '/dashboard' },
  '/profile': { label: 'Mi Perfil', parent: '/dashboard' },
  // Plataforma · DATEC. Los nombres siguen a los de BarraPlataforma: si la miga
  // dice 'Usuarios Global' y la barra dice 'Personas', el usuario duda de si
  // llegó a donde quería.
  /*
    🔴 `/plataforma` NO lleva `parent`, a propósito: es la raíz de su propio
    plano, no una pantalla que cuelgue del portal.

    Con `parent: '/dashboard'` la miga de pan ofrecía un «Dashboard» que apunta
    a una ruta de portal, y adentro del panel no hay empresa: el enlace llevaba
    a `/dashboard` pelado y ahí se dibujaba el portal entero, fuera de toda
    empresa. Es exactamente la ambigüedad que este frente vino a sacar, dada
    vuelta. Para volver al trabajo de una empresa está «Volver a los portales»,
    en la barra del propio panel.
  */
  '/plataforma': { label: 'Panel' },
  '/plataforma/empresas': { label: 'Empresas', parent: '/plataforma' },
  '/plataforma/comparacion': { label: 'Comparación', parent: '/plataforma' },
  '/plataforma/personas': { label: 'Personas', parent: '/plataforma' },
  '/plataforma/personas/:id': { label: 'Detalle', parent: '/plataforma/personas' },
  '/plataforma/planes': { label: 'Planes', parent: '/plataforma' },
  // El catálogo de Prixia (plan 297): sin entrada, la lista decía «Panel › Detalle».
  // La ficha (`/plataforma/tiendas/:id`) cae en «Tiendas de Prixia › Detalle».
  '/plataforma/tiendas': { label: 'Tiendas de Prixia', parent: '/plataforma' },
  '/plataforma/lecturas': { label: 'Lecturas', parent: '/plataforma' },
  '/plataforma/invitaciones': { label: 'Invitaciones', parent: '/plataforma' },
  '/plataforma/solicitudes': { label: 'Solicitudes', parent: '/plataforma' },
  /*
    🔴 Sin entrada acá, la miga no dice el nombre de la página: dice literalmente
    'Detalle', que es el rótulo de descarte del final de buildBreadcrumbs. Le pasaba
    a estas dos desde que existen.
  */
  '/plataforma/configuracion': { label: 'Configuración', parent: '/plataforma' },
  '/plataforma/bugs': { label: 'Reportes de bugs', parent: '/plataforma' },
  // Ayuda
  '/help': { label: 'Centro de Ayuda', parent: '/dashboard' },
  '/help/curso-implementacion': { label: 'Curso de Implementación', parent: '/help' },
  '/tutorial': { label: 'Tutorial', parent: '/dashboard' },
  '/integrations': { label: 'Integraciones', parent: '/dashboard' },
};

export function buildBreadcrumbs(pathname: string): { label: string; href: string }[] {
  const crumbs: { label: string; href: string }[] = [];

  // Try exact match first, then check for dynamic routes (e.g., /quotes/:id)
  const current = pathname;
  const routeInfo = ROUTE_LABELS[current];

  if (routeInfo) {
    // Build chain from current to root
    let path: string | undefined = current;
    while (path && ROUTE_LABELS[path]) {
      crumbs.unshift({ label: ROUTE_LABELS[path].label, href: path });
      path = ROUTE_LABELS[path].parent;
    }
  } else {
    // Dynamic route: walk segments backward to find the most specific
    // matching parent. Ex: /configuracion/equipos/abc → /configuracion/equipos
    // first, then /configuracion. Soporta tambien /pricer-datec/:id.
    const segments = pathname.split('/').filter(Boolean);
    let parentPath: string | undefined;
    for (let i = segments.length - 1; i >= 1; i--) {
      const candidate = '/' + segments.slice(0, i).join('/');
      if (ROUTE_LABELS[candidate]) {
        parentPath = candidate;
        break;
      }
    }
    if (parentPath) {
      let path: string | undefined = parentPath;
      while (path && ROUTE_LABELS[path]) {
        crumbs.unshift({ label: ROUTE_LABELS[path].label, href: path });
        path = ROUTE_LABELS[path].parent;
      }
      crumbs.push({ label: 'Detalle', href: pathname });
    }
  }

  return crumbs;
}

const dropdownVariants = {
  hidden: { opacity: 0, scale: 0.95, y: -4 },
  visible: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 0.95, y: -4 },
};

function Breadcrumbs() {
  const location = useLocation();
  const crumbs = buildBreadcrumbs(location.pathname);

  if (crumbs.length === 0) {
    return <span className="text-sm font-semibold text-foreground">Prixia</span>;
  }

  /*
    Rediseño del 18-oct (artefacto): «Inicio / Mi empresa / Usuarios», con la
    barra como separador y la última en negrita. La cadena ya empieza en Inicio
    (`/dashboard`) para casi todas las pantallas; si no, se antepone.
  */
  // El panel de plataforma es otro plano: su raíz es «Panel», nunca el Inicio de un portal.
  const conInicio =
    crumbs[0].href === '/dashboard' || crumbs[0].href.startsWith('/plataforma')
      ? crumbs
      : [{ label: 'Inicio', href: '/dashboard' }, ...crumbs];

  return (
    <nav aria-label="Migas de pan" className="flex min-w-0 items-center gap-2 text-sm">
      {conInicio.map((crumb, i) => {
        const isLast = i === conInicio.length - 1;
        return (
          <div key={crumb.href} className="flex min-w-0 items-center gap-2">
            {i > 0 && (
              <span className="text-muted-foreground/60" aria-hidden="true">
                /
              </span>
            )}
            {isLast ? (
              <span className="truncate font-semibold text-foreground" aria-current="page">
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.href}
                className="truncate text-muted-foreground transition-colors hover:text-foreground"
              >
                {crumb.label}
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
}

export function Navbar() {
  const abrirMenu = useSidebarStore((s) => s.abrir);
  const { canSeeMenu } = useAuth();
  const { theme, cycleTheme } = useThemeStore();
  const [showNotifications, setShowNotifications] = useState(false);
  const navigate = useNavigate();
  const notifRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    startPolling,
    stopPolling,
  } = useNotificationStore();

  // Start/stop polling when component mounts/unmounts
  useEffect(() => {
    startPolling();
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    if (showNotifications) {
      fetchNotifications();
    }
  }, [showNotifications, fetchNotifications]);

  // Close dropdowns on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notification: (typeof notifications)[0]) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    setShowNotifications(false);
    const destino = destinoDeLaNotificacion(notification, canSeeMenu('pricer-datec'));
    // Spec 294: con cambios propios sin aplicar en Reglas, salir pregunta antes.
    if (destino) pedirSalida(() => navigate(destino));
  };

  return (
    <header className="flex h-16 shrink-0 items-center justify-between gap-3 border-b border-border bg-card px-4 md:px-8">
      {/* Left side - Breadcrumbs */}
      <div className="flex items-center gap-2 min-w-0">
        {/*
          El único acceso al menú en teléfono, donde el menú es un cajón cerrado.
          En pantalla grande no aparece: ahí el menú ya está a la vista y este
          botón sería un segundo control para lo mismo.
        */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden shrink-0"
          onClick={abrirMenu}
          aria-label="Abrir menú de navegación"
        >
          <Menu className="w-5 h-5" aria-hidden="true" />
        </Button>
        {/*
          Las migas de pan se ocultan en teléfono. En 390 px competían por el
          espacio con los íconos de la derecha y se superponían con ellos —el
          título de la página se leía encima del botón de tema—. No se pierde
          nada: el nombre de la pantalla está en el <h1>, tres centímetros más
          abajo, y para volver está el menú.
        */}
        <div className="hidden sm:block min-w-0">
          <Breadcrumbs />
        </div>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2">
        {/* Global Search */}
        <GlobalSearch />

        {/* Organization Switcher: en el celular no entra junto a la campana (se cambia de equipo en una pantalla ancha). */}
        <div className="hidden md:block">
          <OrgSwitcher />
        </div>

        {/* Ayuda y «Reportar un problema» (entran por portal; ver ranura-de-la-barra). */}
        <RanuraDeAyuda />

        {/* Theme Toggle */}
        <Button
          variant="outline"
          size="icon"
          className="h-10 w-10 rounded-full"
          onClick={cycleTheme}
          title={
            theme === 'light' ? 'Modo claro' : theme === 'dark' ? 'Modo oscuro' : 'Modo sistema'
          }
          aria-label={`Cambiar el tema (ahora: ${
            theme === 'light' ? 'claro' : theme === 'dark' ? 'oscuro' : 'el del sistema'
          })`}
        >
          {theme === 'light' && <Sun className="w-5 h-5" />}
          {theme === 'dark' && <Moon className="w-5 h-5" />}
          {theme === 'system' && <Monitor className="w-5 h-5" />}
        </Button>

        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <Button
            variant="outline"
            size="icon"
            className="relative h-10 w-10 rounded-full"
            onClick={() => setShowNotifications(!showNotifications)}
            aria-label={
              unreadCount > 0 ? `Notificaciones: ${unreadCount} sin leer` : 'Notificaciones'
            }
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <Badge
                variant="destructive"
                className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-transparent bg-destructive p-0 px-1 text-2xs text-destructive-foreground"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </Button>

          {/* Notifications Dropdown */}
          <AnimatePresence>
            {showNotifications && (
              <motion.div
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={{ duration: 0.15, ease: 'easeOut' }}
                className="absolute right-0 mt-2 w-96 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50"
              >
                <div className="p-4 border-b border-border flex items-center justify-between">
                  <h3 className="font-semibold">Notificaciones</h3>
                  {unreadCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs gap-1 h-7"
                      onClick={() => markAllAsRead()}
                    >
                      <CheckCheck className="w-3.5 h-3.5" />
                      Marcar todas como leídas
                    </Button>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {isLoading && notifications.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <p className="text-sm">Cargando...</p>
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <Bell className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No hay notificaciones</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-4 border-b border-border hover:bg-accent cursor-pointer group relative ${
                          !notification.isRead ? 'bg-primary/5' : ''
                        }`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start gap-2">
                          {!notification.isRead && (
                            <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                          )}
                          {/* Cada aviso con su ícono y el color de su módulo (26-oct). */}
                          <IconoDeAviso tipo={notification.type} className="mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{notification.title}</p>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              {notification.message}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatRelativeTime(notification.createdAt)}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
}

export default Navbar;
